<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CaretUpIcon from '@iconify-vue/ph/caret-up';
import CheckIcon from '@iconify-vue/ph/check';
import EyeIcon from '@iconify-vue/ph/eye';
import ProhibitIcon from '@iconify-vue/ph/prohibit';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import {
    formatNumber,
    formatPercentage,
    formatPrice,
    formatTime,
} from '@js/utils/formatter';
import { computed } from 'vue';

const {
    title,
    subtitle,
    size = 'medium',
    value,
    compareValue,
    compareData,
    type = 'number',
    withPlus = false,
    withDecimals = true,
    icon,
    hrefLink,
    inverseCompareColor = false,
    noCompareColor = false,
    color,
    compareColor,
    suffixValue,
    compare = true,
    suffixCompareValue,
    currency = '€',
} = defineProps({
    title: {
        type: String,
        required: true,
    },
    subtitle: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    value: {
        type: [Number, String, Boolean, Object],
        required: false,
    },
    compareValue: {
        type: Number,
        required: false,
    },
    compareData: {
        type: Array,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    withPlus: {
        type: Boolean,
        required: false,
    },
    withDecimals: {
        type: Boolean,
        required: false,
    },
    icon: {
        type: [Object, Function],
        required: false,
    },
    hrefLink: {
        type: String,
        required: false,
    },
    inverseCompareColor: {
        type: Boolean,
        required: false,
    },
    noCompareColor: {
        type: Boolean,
        required: false,
    },
    color: {
        type: String,
        required: false,
    },
    compareColor: {
        type: String,
        required: false,
    },
    suffixValue: {
        type: String,
        required: false,
    },
    compare: {
        type: Boolean,
        required: false,
    },
    suffixCompareValue: {
        type: String,
        required: false,
    },
    currency: {
        type: String,
        required: false,
    },
});

const formattedValue = computed(() => {
    return formatValue(value);
});

const formattedCompareValue = computed(() => {
    return formatValue(compareValue);
});

function formatValue(innerValue) {
    if (innerValue === undefined || innerValue === null) {
        return null;
    }

    if (typeof innerValue === 'string') {
        return innerValue;
    }

    if (type === 'number') {
        return formatNumber(innerValue, withDecimals);
    }

    if (type === 'price') {
        return formatPrice(innerValue, withDecimals, 1, currency);
    }

    if (type === 'percentage') {
        return formatPercentage(innerValue, withPlus, withDecimals);
    }

    if (
        type === 'time' &&
        innerValue.hours !== undefined &&
        innerValue.minutes !== undefined
    ) {
        return formatTime(innerValue.hours, innerValue.minutes);
    }

    return innerValue;
}

const comparePercentage = computed(() => {
    if (compareValue === undefined) {
        return null;
    }

    if (compareValue === 0) {
        return 0;
    }

    return ((value - compareValue) / Math.abs(compareValue)) * 100;
});
</script>

<template>
    <div
        v-if="size === 'small'"
        class="flex flex-col items-start rounded-lg border border-gray-200 bg-white px-3 py-2.5"
    >
        <span
            class="mb-1.5 text-[11px] font-semibold tracking-wide text-gray-400 uppercase"
        >
            {{ title }}
        </span>

        <span
            class="text-lg leading-none font-bold text-gray-900"
            :class="{
                'text-green-500': color === 'green',
                'text-yellow-500': color === 'yellow',
                'text-orange-500': color === 'orange',
                'text-red-500': color === 'red',
            }"
        >
            <template v-if="type === 'boolean'">
                <CheckIcon v-if="value" class="size-5 text-green-600" />
                <ProhibitIcon v-else class="size-5 text-red-600" />
            </template>

            <template v-else>
                {{ formattedValue }}

                <template v-if="suffixValue">
                    {{ suffixValue }}
                </template>
            </template>
        </span>
    </div>

    <div
        v-else
        class="relative flex items-center justify-between gap-4 rounded-sm border border-gray-200 bg-white px-4 py-3"
    >
        <div class="flex h-full min-w-0 flex-col">
            <div class="text-gray-500">
                {{ title }}
            </div>

            <div class="text-sm text-gray-400">
                <template v-if="subtitle">
                    {{ subtitle }}
                </template>

                <template v-else> &nbsp;</template>
            </div>

            <div
                class="mt-auto inline-flex flex-wrap-reverse items-center gap-2 pt-6 whitespace-nowrap"
            >
                <div
                    class="min-w-0 text-gray-900"
                    :class="{
                        'text-base font-medium break-words whitespace-normal':
                            typeof value === 'string' && type !== 'time',
                        'text-2xl font-bold whitespace-nowrap':
                            typeof value !== 'string' || type === 'time',
                        'text-green-500': color === 'green',
                        'text-yellow-500': color === 'yellow',
                        'text-orange-500': color === 'orange',
                        'text-red-500': color === 'red',
                    }"
                >
                    <template v-if="type === 'boolean'">
                        <CheckIcon v-if="value" class="size-6 text-green-600" />
                        <ProhibitIcon v-else class="size-6 text-red-600" />
                    </template>

                    <template v-else>
                        {{ formattedValue }}

                        <template v-if="suffixValue">
                            {{ suffixValue }}
                        </template>
                    </template>
                </div>

                <div
                    v-if="compare && compareValue && comparePercentage"
                    class="flex items-center gap-1 rounded-full px-2 py-0.5 text-sm"
                    :class="{
                        'bg-green-100 text-green-600':
                            (comparePercentage > 0 &&
                                !inverseCompareColor &&
                                !noCompareColor) ||
                            (comparePercentage < 0 &&
                                inverseCompareColor &&
                                !noCompareColor),
                        'bg-red-200 text-red-600':
                            (comparePercentage < 0 &&
                                !inverseCompareColor &&
                                !noCompareColor) ||
                            (comparePercentage > 0 &&
                                inverseCompareColor &&
                                !noCompareColor),
                        'bg-gray-200 text-gray-600': noCompareColor,
                    }"
                >
                    <CaretUpIcon v-if="comparePercentage > 0" class="size-4" />
                    <CaretDownIcon v-else class="size-4" />

                    {{ formatPercentage(comparePercentage, true) }}
                </div>
            </div>

            <div
                v-if="compareValue !== undefined && compareValue !== null"
                class="font-bold text-gray-700"
                :class="{
                    'text-green-500': compareColor === 'green',
                    'text-yellow-500': compareColor === 'yellow',
                    'text-orange-500': compareColor === 'orange',
                    'text-red-500': compareColor === 'red',
                }"
            >
                {{ formattedCompareValue }}

                <template v-if="suffixCompareValue">
                    {{ suffixCompareValue }}
                </template>
            </div>

            <template
                v-else-if="compareData"
                v-for="(compareDateItem, index) in compareData"
                :key="index"
            >
                <div class="font-bold text-gray-700">
                    {{ formatValue(compareDateItem.value) }}

                    <template v-if="compareDateItem.suffix">
                        {{ compareDateItem.suffix }}
                    </template>
                </div>
            </template>
        </div>

        <div class="flex h-full flex-col items-end gap-4">
            <div
                v-if="icon"
                class="mb-auto flex size-10 items-center justify-center rounded-full bg-blue-200 p-2 drop-shadow-lg"
            >
                <component :is="icon" class="size-6" />
            </div>

            <div class="mt-auto flex flex-col-reverse gap-2">
                <slot name="actions" />

                <ActionButton
                    v-if="hrefLink"
                    :href="hrefLink"
                    size="small"
                    type="secondary"
                    class="size-8"
                    :icon="EyeIcon"
                />
            </div>
        </div>
    </div>
</template>
